import {FormlyFieldConfig, FormlyTemplateOptions} from '@ngx-formly/core';
import {AbstractControl, ValidationErrors} from '@angular/forms';

export function TimeFormatValidator(control: AbstractControl): ValidationErrors {
  return !control.value || /^(?:[0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(control.value) ? null : {timeFormat: true};
}

export function TimeFormatValidatorMessage(error: any, fieldConfig: FormlyFieldConfig) {
  return `"${fieldConfig.formControl.value}" must be in the format HH:MM.`;
}

export function NumberValidator(control: AbstractControl): ValidationErrors {
  return !control.value || /^-?[0-9]*\.?[0-9]+$/.test(control.value) ? null : {number: true};
}

export function NumberValidatorMessage(error: any, fieldConfig: FormlyFieldConfig) {
  return `"${fieldConfig.formControl.value}" must be a number.`;
}

export interface Option {
  value: any;
  label: string;
}

export const formlyRow2 = (fieldConfig: FormlyFieldConfig): FormlyFieldConfig => {
  return formlyRow([fieldConfig, formly.empty()]);
};

export const formlyRow = (fieldConfig: FormlyFieldConfig[] | FormlyFieldConfig): FormlyFieldConfig => {
  if (Array.isArray(fieldConfig)) {
    fieldConfig.forEach(fc => fc.className += ' col');
    return {
      fieldGroupClassName: 'grid',
      fieldGroup: fieldConfig,
      templateOptions: {},
    };
  } else {
    return fieldConfig;
  }
};

export const options = (items: any[], label: string = null, value = null): Option[] => {
  if (label) {
    return items.map(item => ({
      label: item.hasOwnProperty(label) ? item[label] : '-',
      value: value ? item[value] : item
    } as Option));
  } else {
    return items.map(item => ({label: item, value: item} as Option));
  }
};

export const optionsBoolean = (): Option[] => [
  {label: 'Yes', value: true},
  {label: 'No', value: false},
];

function toOptions(opts: Option[] | string[]): Option[] {
  if (opts.length > 0 && typeof opts[0] === 'string') {
    return opts.map(item => ({label: item, value: item}));
  }
  // @ts-ignore
  return opts;
}

function nestedField(
  key: string,
  label: string = '',
  fieldGroup: FormlyFieldConfig[],
  templateOptions: FormlyTemplateOptions = {},
  wrappers: string[] = [],
): FormlyFieldConfig {
  templateOptions = {...templateOptions, ...{label}};
  return {key, fieldGroup, templateOptions} as FormlyFieldConfig;
}

function field(
  key: string,
  type: string = 'input',
  label: string = '',
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  const returner: FormlyFieldConfig = {key, type};
  returner.templateOptions = Object.assign({label, ...templateOptions});

  const className = `formly-${key}`;

  otherOptions.className = otherOptions.className ? `${otherOptions.className} ${className}` : className;

  return {...returner, ...otherOptions};
}

function requiredField(
  key: string,
  type: string = 'input',
  label: string = '',
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return field(
    key,
    type,
    label,
    {...templateOptions, required: true},
    otherOptions
  );
}

function html(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(key, 'html', label, templateOptions, otherOptions);
}

function requiredHtml(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return requiredField(key, 'html', label, templateOptions, otherOptions);
}

function text(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {}
): FormlyFieldConfig {
  return field(key, 'input', label, templateOptions);
}

function requiredText(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return requiredField(key, 'input', label, templateOptions, otherOptions);
}

function requiredUppercaseText(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return requiredField(key, 'input', label, {
    ...templateOptions,
    required: true,
  }, {
    ...otherOptions,
    hooks: {
      onInit: (fieldForm) => {
        if (fieldForm.formControl) {
          fieldForm.formControl.valueChanges.subscribe(value => {
            if (value) {
              const transformed = value.toUpperCase().replace(/\s+/g, '_');
              if (value !== transformed) {
                fieldForm.formControl?.setValue(transformed, {emitEvent: false});
              }
            }
          });
        }
      }
    }
  });
}

function formatUpperUnderscore(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '_');
}

function number(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(key, 'input', label, templateOptions, {
    className: 'w-3',
    validators: {
      validation: [NumberValidator],
    },
    ...otherOptions
  });
}

function requiredNumber(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {defaultValue: 0}
): FormlyFieldConfig {
  return number(
    key,
    label,
    {...templateOptions, required: true},
    otherOptions
  );
}

function numberNonRequired(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {defaultValue: 0}
): FormlyFieldConfig {
  return number(
    key,
    label,
    {...templateOptions, required: false},
    otherOptions
  );
}

function chips(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions = {}
): FormlyFieldConfig {
  return requiredField(key, 'chips', label, templateOptions, otherOptions);
}

function multiLangText(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions = {wrappers: ['no-wrap']}
): FormlyFieldConfig {
  return {
    key,
    type: 'tabs',
    fieldGroup: [
      requiredField('en', 'input', 'EN', templateOptions, otherOptions),
      field('th', 'input', 'TH', templateOptions, otherOptions),
      field('ja', 'input', 'JP', templateOptions, otherOptions),
      field('ko', 'input', 'KO', templateOptions, otherOptions)
    ],
    templateOptions: {
      label
    },
  };
}

function multiLangTextNonRequire(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions = {wrappers: ['no-wrap']}
): FormlyFieldConfig {
  return {
    key,
    type: 'tabs',
    fieldGroup: [
      field('en', 'input', 'EN', {...templateOptions, required: false}, otherOptions),
      field('th', 'input', 'TH', {...templateOptions, required: false}, otherOptions),
      field('ja', 'input', 'JP', {...templateOptions, required: false}, otherOptions),
      field('ko', 'input', 'KO', {...templateOptions, required: false}, otherOptions),
    ],
    templateOptions: {
      label
    },
  };
}


function multiLangHtml(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions = {wrappers: ['no-wrap']}
): FormlyFieldConfig {
  return {
    key,
    type: 'tabs',
    fieldGroup: [
      html('en', 'EN', templateOptions, otherOptions),
      html('th', 'TH', templateOptions, otherOptions),
      html('ja', 'JA', templateOptions, otherOptions),
      html('ko', 'KO', templateOptions, otherOptions)
    ],
    templateOptions: {
      label
    },
  };
}

function multiLangTextArea(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {rows: 10, cols: 100},
  otherOptions = {wrappers: ['no-wrap']}
): FormlyFieldConfig {
  return {
    key,
    type: 'tabs',
    fieldGroup: [
      textArea('en', 'EN', templateOptions, otherOptions),
      textArea('th', 'TH', templateOptions, otherOptions),
      textArea('ja', 'JA', templateOptions, otherOptions),
      textArea('ko', 'KO', templateOptions, otherOptions)
    ],
    templateOptions: {
      label
    }
  };
}

function password(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions = {}
): FormlyFieldConfig {
  return requiredText(key, label, {...templateOptions, type: 'password'}, otherOptions);
}

function hidden(key: string): FormlyFieldConfig {
  return {key, type: 'input', hide: true};
}

function checkbox(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(key, 'checkbox', label, templateOptions, {...otherOptions, defaultValue: false});
}

function requiredCheckbox(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(key, 'checkbox', label, templateOptions, {...otherOptions, defaultValue: false});
}

function select(key: string, label: string = '', opts: Option[]) {
  return field(key, 'select', label, {options: [{label: '-'}, ...opts]});
}

function requiredSelectCategory(key: string,
                                label: string = '',
                                templateOptions: FormlyTemplateOptions = {},
                                otherOptions = {},
                                modelId = 'id'
): FormlyFieldConfig {
  return customSelectObjectField(key, label, {
    ...templateOptions,
    required: true,
    source: 'category-types'
  }, otherOptions, modelId);
}

function selectSupplier(key: string,
                        label: string = '',
                        templateOptions: FormlyTemplateOptions = {},
                        otherOptions = {},
                        modelId = 'id'
): FormlyFieldConfig {
  return customSelectObjectField(key, label, {
    ...templateOptions,
    required: false,
    source: 'supplier'
  }, otherOptions, modelId);
}

function customSelectField(key: string, label: string = '',
                           templateOptions: FormlyTemplateOptions = {},
                           otherOptions = {},
                           modelId = 'id') {
  return field(key, 'custom-select', label,
    {
      ...templateOptions,
      modelId,
    }, otherOptions);
}

function customSelectObjectField(key: string, label: string = '',
                                 templateOptions: FormlyTemplateOptions = {},
                                 otherOptions = {},
                                 modelId = 'id') {
  return field(key, 'custom-select', label, {
    ...templateOptions,
    compareWith: (o1, o2) => {
      return (o1 && o2 && modelId ? o1[modelId] === o2[modelId] : o1 === o2);
    }
  }, otherOptions);
}

function requiredSelect(
  key: string,
  label: string = '',
  opts: Option[] | string[],
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  const options1 = toOptions(opts as Option[] | string[]);
  return field(key, 'select', label, {
      ...templateOptions,
      options: options1,
      required: true
    },
    otherOptions);
}

function selectNonrequired(
  key: string,
  label: string = '',
  opts: Option[] | string[],
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  const options1 = toOptions(opts as Option[] | string[]);
  return field(key, 'select', label, {
      ...templateOptions,
      options: options1,
      required: false
    },
    otherOptions);
}

function requiredSelectObject(
  key: string,
  label: string = '',
  options: Option[] = [],
  templateOptions: FormlyTemplateOptions = {},
  modelId = 'id',
): FormlyFieldConfig {
  return field(key, 'select', label, {
    ...templateOptions,
    options,
    required: true,
    valueProp: (option) => option.value,
    compareWith: (o1, o2) => o1[modelId] === o2[modelId],
  });
}

function multiSelect(
  key: string,
  label: string = '',
  opts: Option[] | string[],
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  const type = options.length > 10 ? 'listbox' : 'multiselect';
  const options1 = toOptions(opts as Option[] | string[]);

  return field(key, type, label, {
    options: options1,
    multiple: true,
    templateOptions,
    otherOptions
  });
}

function requiredMultiSelect(
  key: string,
  label: string = '',
  opts: Option[],
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return multiSelect(key, label, opts, {...templateOptions, required: true}, otherOptions);
}

function requiredListBox(
  key: string,
  label: string = '',
  options: Option[],
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return field(key, 'listbox', label, {
    options,
    multiple: true,
    templateOptions,
    otherOptions
  });
}

function radio(
  key: string,
  label: string = '',
  opts: Option[],
  templateOptions: FormlyTemplateOptions = {}
) {
  return field(key, 'radio', label, {options: opts, ...templateOptions});
}

function requiredRadio(
  key: string,
  label: string = '',
  opts: any[],
  templateOptions: FormlyTemplateOptions = {}
) {
  return field(key, 'radio', label, {
    options: opts,
    ...templateOptions,
    required: true
  });
}

// To use with java 's LocalDate, need to convert LocalDate to string using localDate.toDMY()
function date(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return field(key, 'datepicker', label, templateOptions, otherOptions);
}

function requiredDate(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return field(
    key,
    'datepicker',
    label,
    {...templateOptions, required: true},
    otherOptions
  );
}

/*Date-Time*/

// Compatible with java 's ZonedDateTime, Date
function dateTime(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(
    key, 'datepicker',
    label,
    {...templateOptions, showTime: true},
    otherOptions
  );
}

function requiredDateTime(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(
    key,
    'datepicker',
    label,
    {...templateOptions, showTime: true, required: true},
    otherOptions
  );
}

/*Time*/
function time(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(
    key, 'datepicker',
    label,
    {...templateOptions, timeOnly: true, dataType: 'string'},
    otherOptions
  );
}

function requiredTime(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(
    key,
    'datepicker',
    label,
    {...templateOptions, timeOnly: true, dataType: 'string', required: true},
    otherOptions
  );
}

function localDate(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(
    key, 'datepicker',
    label,
    {...templateOptions, dataType: 'string'},
    otherOptions
  );
}

function requiredLocalDate(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  return field(
    key,
    'datepicker',
    label,
    {...templateOptions, dataType: 'string', required: true},
    otherOptions
  );
}

function textArea(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return field(key, 'textarea', label, templateOptions, otherOptions);
}

function requiredTextArea(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return requiredField(key, 'textarea', label, templateOptions, otherOptions);
}

function file(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return requiredField(key, 'file', label, templateOptions, otherOptions);
}

function fileNonRequire(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return field(key, 'file', label, {...templateOptions, required: false}, otherOptions);
}

function fileInput(
  key: string,
  label: string,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
) {
  return requiredField(key, 'file-input', label, templateOptions, otherOptions);
}

function repeat(
  key: string,
  label: string,
  fieldArray?: FormlyFieldConfig,
  templateOptions: FormlyTemplateOptions = {},
  otherOptions: FormlyFieldConfig = {}
): FormlyFieldConfig {
  const formlyFieldConfig = field(key, 'repeat', label, {showAddRemove: true, ...templateOptions}, otherOptions);
  formlyFieldConfig.fieldArray = fieldArray;
  return formlyFieldConfig;
}

// Internal
function courseSelect(
  key: string,
  label: string = '',
  multiple = false,
  templateOptions: FormlyTemplateOptions = {}
) {
  const defaultValue = multiple ? [] : null;
  return field(key, 'course-select', label, {
    multiple,
    ...templateOptions
  }, {defaultValue});
}

function requiredShopSelect(
  key: string,
  label: string = '',
  multiple = false,
  templateOptions: FormlyTemplateOptions = {}
) {
  const defaultValue = multiple ? [] : null;
  return requiredField(key, 'shop-select', label, {
    multiple,
    ...templateOptions
  }, {defaultValue});
}

//
// function requiredCourseSelect(
//   key: string,
//   label: string = '',
//   multiple = false,
//   templateOptions: FormlyTemplateOptions = {}
// ) {
//   const defaultValue = multiple ? [] : null;
//   return requiredField(key, 'course-select', label, {
//     multiple,
//     ...templateOptions
//   }, {defaultValue});
// }

function requiredCourseSelect(
  key: string,
  label: string = '',
  multiple = false,
  templateOptions: FormlyTemplateOptions = {}
) {
  const defaultValue = multiple ? [] : null;
  return requiredField(key, 'course-select', label, {
    multiple,
    ...templateOptions
  }, {defaultValue});
}


function title(s: string, className?: string, offset = 0): FormlyFieldConfig {
  if (offset > 0) {
    return {
      template: ` <div class="grid">
                <div class="section-label ${className} col-offset-${offset}">${s}</div>
            </div>`,
    };
  } else {
    return {
      template: ` <div class="section-label ${className}">${s}</div>`,
    };
  }
}

function subTitle(s: string, className?: string): FormlyFieldConfig {
  return {
    template: ` <div class="section-label-subtitle">${s}</div>`,
    className
  };
}


function empty(styleClass = ''): FormlyFieldConfig {
  return {
    template: ` <div class="${styleClass}"></div>`,
  };
}

function hr(): FormlyFieldConfig {
  return {
    template: `<hr>`,
  };
}

export const formly = {
  row: formlyRow,
  field,
  title,
  subTitle,
  empty,
  nestedField,
  requiredField,
  text,
  password,
  requiredText,
  number,
  numberNonRequired,
  requiredNumber,
  hidden,
  boolean: checkbox,
  checkbox,
  requiredCheckbox,
  select,
  requiredSelect,
  selectNonrequired,
  requiredSelectObject,
  requiredSelectCategory,
  selectSupplier,
  customSelectObjectField,
  multiSelect,
  requiredMultiSelect,
  requiredListBox,
  radio,
  requiredRadio,
  date,
  requiredDate,
  localDate,
  requiredLocalDate,
  textArea,
  requiredTextArea,
  repeat,
  file,
  fileNonRequire,
  fileInput,
  multiLangText,
  multiLangTextNonRequire,
  multiLangTextArea,
  multiLangHtml,
  html,
  chips,
  hr,
  courseSelect,
  requiredShopSelect,
  requiredCourseSelect,
  requiredUppercaseText,
  time,
  requiredTime,
  dateTime,
  requiredDateTime,
};
